/**
 * Hook to get available coverages from the coverage-summary endpoint.
 *
 * Lives in btx-plan-designer (not the shared plan-grid-core engine) because it
 * depends on `@platform/coverage-overview`, which the shared engine may not.
 */

import { logger } from "@platform/browser-logger";
import { useCoverageOverview } from "@platform/coverage-overview";
import {
  isCoverageName,
  labelToCoverageType,
  useApiClient,
  type CoverageName,
} from "@platform/plan-grid-core";
import { CoverageTypeEnum } from "@platform/types";
import { useMemo } from "react";

import type { AxiosInstance } from "axios";

/**
 * Result type for useGetAvailableCoverages hook
 */
export interface UseGetAvailableCoveragesResult {
  data: CoverageTypeEnum[];
  statusByType: Map<CoverageTypeEnum, string | null>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * `axiosInstance` is optional so callers outside a `PlanDesignerProvider`
 * (which is where `useApiClient` reads it from) can supply their own —
 * `useApiClient(true)` returns undefined instead of throwing in that case.
 */
export const useGetAvailableCoverages = (
  ecoRfpId: string,
  axiosInstance?: AxiosInstance,
): UseGetAvailableCoveragesResult => {
  const contextAxiosInstance = useApiClient(true);

  // Use the coverage overview hook which already sorts the data
  const { coverageQuery } = useCoverageOverview({
    ecoRfpId,
    axiosInstance: axiosInstance ?? contextAxiosInstance,
  });

  // Transform coverage-overview data to CoverageTypeEnum array
  const coverageTypes = useMemo(() => {
    if (!coverageQuery.data) {
      return [];
    }

    return Array.from(
      new Set(
        (coverageQuery.data.allCoverages ?? [])
          .filter((item) => {
            if (!isCoverageName(item.name)) {
              logger.error(
                `[PlanDesigner] Unrecognized coverage type from backend: "${item.name}"`,
              );
              return false;
            }
            return true;
          })
          .map((item) => labelToCoverageType(item.name as CoverageName)),
      ),
    );
  }, [coverageQuery.data]);

  const statusByType = useMemo(
    () =>
      new Map(
        (coverageQuery.data?.allCoverages ?? [])
          .filter((item) => isCoverageName(item.name))
          .map((item): [CoverageTypeEnum, string | null] => [
            labelToCoverageType(item.name as CoverageName),
            item.status,
          ]),
      ),
    [coverageQuery.data],
  );

  return {
    data: coverageTypes,
    statusByType,
    isLoading: coverageQuery.isLoading,
    isError: coverageQuery.isError,
    error: coverageQuery.error,
  };
};
